import { ChangeEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { ExpandMore, MoreVert } from "@material-ui/icons";
import { DictionaryMode } from "contexts/DictionaryToolsContext";
import {
  GetPersonalDictionaryResponse,
  deleteSavedWordMutation,
  personalDictionaryQuery,
  updateSavedWordMutation,
} from "services/dictionary";
import { logError } from "services/logging";
import Action from "components/formElements/Action/Action";
import define from "components/TextSelectionMenu/images/define.svg";
import translate from "components/TextSelectionMenu/images/translate.svg";
import trash from "images/trash.svg";
import quickReferenceAllImg from "images/quick_reference_all.svg";
import audioImg from "../../images/audio.svg";
import { SavedWordInput, WordFormData } from "./model";
import { SentencesExampleDialog } from "./SentencesExampleDialog";
import "./SaveWords.css";

type SaveWordsProps = SavedWordInput & {
  expanded: boolean;
  onModeChanged: (mode: DictionaryMode) => void;
  onDeleteWord: (word: WordFormData) => void;
  onTextChanged: (text: string) => void;
  onChangePanel: (event: ChangeEvent<{}>, expanded: boolean) => void;
};

export const SaveWords = (props: SaveWordsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteWord = useMutation(deleteSavedWordMutation());
  const updateWord = useMutation(updateSavedWordMutation());
  const [openExampleDialog, setOpenExampleDialog] = useState(false);
  const [delayedNotes, setDelayedNotes] = useState<string | null>(null);
  const [wordData, setWordData] = useState<WordFormData>({
    id: props.id || "",
    pos: props.pos || "",
    word: props.word || "",
    definition: props.definition || "",
    translation: props.translation || "",
    audio: props.audio || "",
    notes: props.notes || "",
  });

  useEffect(() => {
    setWordData({
      id: props.id || "",
      pos: props.pos || "",
      word: props.word || "",
      definition: props.definition || "",
      translation: props.translation || "",
      audio: props.audio || "",
      notes: props.notes || "",
    });
  }, [props]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setWordData({ ...wordData, [e.target.name]: e.target.value });
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    if (!wordData.id) {
      return;
    }
    deleteWord.mutate(
      { id: wordData.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: personalDictionaryQuery.queryKeyPrefix,
          });
          props.onDeleteWord(wordData);
        },
        onError: logError,
      }
    );
  };

  const handleShowExampleSentences = () => {
    if (props.id) {
      setOpenExampleDialog(true);
    }
  };

  const patchCachedNotes = (id: string, notes: string) => {
    // Patch every cached page in place instead of invalidating, so saving
    // notes doesn't refetch the list under the user while they keep typing.
    queryClient.setQueriesData<GetPersonalDictionaryResponse>(
      { queryKey: personalDictionaryQuery.queryKeyPrefix },
      data =>
        data && {
          ...data,
          list: data.list.map(word => (word.id === id ? { ...word, notes } : word)),
        }
    );
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => setDelayedNotes(wordData.notes), 500);
    return () => clearTimeout(timeoutId);
  }, [wordData.notes]);

  useEffect(() => {
    if (wordData.id && props.expanded && delayedNotes !== null) {
      const id = wordData.id;
      updateWord.mutate(
        { id, payload: { notes: delayedNotes } },
        {
          onSuccess: () => patchCachedNotes(id, delayedNotes),
          onError: logError,
        }
      );
    }
    // eslint-disable-next-line
  }, [delayedNotes]);

  return (
    <div className="save-item">
      <AccordionView
        expanded={props.expanded}
        onChange={props.onChangePanel}
        header={
          <SaveWordHeader
            pos={props.pos}
            word={props.word}
            audio={props.audio}
            onDelete={handleDelete}
            onModeChanged={props.onModeChanged}
            onTextChanged={props.onTextChanged}
            onChangePanel={props.onChangePanel}
          />
        }
      >
        <MyNotes value={wordData.notes} label={t("Tool.myNote")} onChange={onChange} />
        <button className="explain-button" onClick={handleShowExampleSentences}>
          <img src={quickReferenceAllImg} alt="quick-reference-all" />
          <span>{t("Tool.showExample")}</span>
        </button>
      </AccordionView>
      {openExampleDialog && props.id && (
        <SentencesExampleDialog
          open={openExampleDialog}
          word={{ id: props.id, word: props.word }}
          onClose={() => setOpenExampleDialog(false)}
        />
      )}
    </div>
  );
};

type SaveWordHeaderProps = {
  audio?: string | null;
  word: string;
  pos: string | null;
  onDelete: (e: MouseEvent) => void;
  onModeChanged?: (mode: DictionaryMode) => void;
  onTextChanged?: (text: string) => void;
  onChangePanel?: (event: ChangeEvent<{}>, expanded: boolean) => void;
};

const SaveWordHeader = (props: SaveWordHeaderProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onModeChanged = (mode: DictionaryMode) => () => {
    if (props.onTextChanged) {
      props.onTextChanged(props.word);
    }

    setTimeout(() => {
      if (props.onModeChanged) {
        props.onModeChanged(mode);
      }
    }, 100);
  };

  const handleOpenActionMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const handleModeChanged = (event: MouseEvent, mode: DictionaryMode) => {
    event.stopPropagation();
    onModeChanged(mode)();
    handleCloseActionMenu();
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    props.onDelete(event);
    handleCloseActionMenu();
  };

  return (
    <div className="save-word-header">
      <div className="save-word-header-container">
        <Pronunciation source={props.audio} />
        <div className="sub-header-container">
          <span>
            <span className="def title" title={props.word}>
              {props.word}
            </span>
          </span>
          <span className="speech" title={props.pos ?? undefined}>
            {props.pos}
          </span>
        </div>
      </div>

      <div className="save-word-header-actions">
        <Action className="btn action-with-image" onClick={onModeChanged("define")}>
          <img src={define} height={20} width={18} alt="save word" />
        </Action>
        <Action className="btn action-with-image" onClick={onModeChanged("translate")}>
          <img src={translate} height={20} width={18} alt="save word" />
        </Action>
        <Action className="btn action-with-image" onClick={props.onDelete}>
          <img src={trash} height={20} width={18} alt="content" />
        </Action>
      </div>

      <div className="save-word-header-mobile-actions">
        <IconButton className="save-word-header-expand">
          <ExpandMore />
        </IconButton>
        <IconButton className="menu-button" onClick={handleOpenActionMenu}>
          <MoreVert />
        </IconButton>
        <Menu
          className="save-words-mobile-action-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={(event: MouseEvent) => handleModeChanged(event, "define")}>
            <img src={define} height={20} width={18} alt="save word" />
            {t("Tool.Define")}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={(event: MouseEvent) => handleModeChanged(event, "translate")}
          >
            <img src={translate} height={20} width={18} alt="save word" />
            {t("Tool.Translate")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete}>
            <img src={trash} height={20} width={18} alt="delete" />
            {t("Tool.Delete")}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

type PronunciationProps = {
  source?: string | null;
};

const Pronunciation = (props: PronunciationProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const onAudioPlay = (e: MouseEvent) => {
    e.stopPropagation();
    audioRef.current?.play();
  };

  return (
    <div className={"audio-container"}>
      <Action className="audio-btn" onClick={onAudioPlay}>
        <img src={audioImg} height={18} width={17} alt="audio" />
      </Action>
      <audio controlsList="nodownload" ref={audioRef}>
        <source src={props.source ?? undefined} />
      </audio>
    </div>
  );
};

type MyNotesProps = {
  value: string;
  label: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const MyNotes = (props: MyNotesProps) => {
  const { t } = useTranslation();
  return (
    <div className={"my-notes-text"}>
      <TextField
        id="outlined-basic"
        name="notes"
        size="small"
        fullWidth
        value={props.value}
        onChange={props.onChange}
        placeholder={`${t("Tool.enterNoteHere")}...`}
        label={props.label}
        variant="outlined"
        color={"primary"}
      />
    </div>
  );
};

type AccordionViewProps = {
  header: ReactNode;
  expanded: boolean;
  children: ReactNode;
  onChange: (event: ChangeEvent<{}>, expanded: boolean) => void;
};

const AccordionView = (props: AccordionViewProps) => {
  return (
    <Accordion
      className="accordion-container"
      expanded={props.expanded}
      onChange={props.onChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-content"
        id="panel-header"
        className="accordion-summary"
      >
        {props.header}
      </AccordionSummary>
      <AccordionDetails>
        <div className="accordion-details">{props.children}</div>
      </AccordionDetails>
    </Accordion>
  );
};

// The notes field is a plain MUI TextField: the app-level muiTheme (Era-3
// palette) owns its focus/label colors — the old per-file teal withStyles
// override is gone with the Era-1 skin.
